import React, { useState } from "react";

import { Button } from "../../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { COUNTRIES, type Country } from "../constants/countries";

interface CountrySelectorProps {
  selectedCountry: Country;
  onCountryChange: (countryCode: string) => void;
  disabled?: boolean;
  className?: string;
}

export function CountrySelector({
  selectedCountry,
  onCountryChange,
  disabled = false,
  className = "",
}: CountrySelectorProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`flex h-10 w-[105px] justify-between rounded-md border border-input dark:border-neutral-700 bg-background dark:bg-neutral-900 px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-sm ${className}`}
        >
          <span className="flex items-center gap-1">
            <span className="text-sm">{selectedCountry.flag}</span>
            <span className="text-sm">{selectedCountry.prefix}</span>
          </span>
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 shrink-0 opacity-50"
          >
            <path
              d="m4.93179 5.43179 2.86821 2.86821 2.86821-2.86821.67857.67857L7.8 10.0607 3.25322 5.51393l.67857-.67857z"
              fill="currentColor"
              fillRule="evenodd"
              clipRule="evenodd"
            ></path>
          </svg>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-0 bg-background dark:bg-neutral-900"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search countries..." className="h-9" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.prefix} ${country.code}`}
                  onSelect={() => {
                    onCountryChange(country.code);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <span>{country.flag}</span>
                  <span className="font-mono text-sm">{country.prefix}</span>
                  <span className="text-sm text-muted-foreground">
                    {country.name}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
